
///
/// EarthBrowser - Protected Component
///
/// Created: 2025-11-15
/// Copyright (c) 2025 Geodata Labs
///

import React from "react";
import {
	useHasPermission,
	useHasAnyPermission,
	useHasAllPermissions,
} from "@/hooks/usePermissions";

interface ProtectedComponentProps {
	permission?: string;
	permissions?: string[];
	requireAll?: boolean;
	fallback?: React.ReactNode;
	children: React.ReactNode;
}

/**
 * Component that only renders if user has required permissions
 */
export function ProtectedComponent({
	permission,
	permissions,
	requireAll = false,
	fallback = null,
	children,
}: ProtectedComponentProps) {
	// Always call hooks unconditionally
	const hasSinglePermission = useHasPermission(permission || "");
	const hasAnyPermissions = useHasAnyPermission(permissions || []);
	const hasAllPermissionsResult = useHasAllPermissions(permissions || []);

	// Determine if user has required permissions
	const hasPermissionCheck = permission ? hasSinglePermission : true;
	const hasPermissionsCheck = permissions
		? requireAll
			? hasAllPermissionsResult
			: hasAnyPermissions
		: true;

	if (!hasPermissionCheck || !hasPermissionsCheck) {
		return <>{fallback}</>;
	}

	return <>{children}</>;
}

/**
 * Component that only renders if user is admin
 */
export function AdminOnly({ children }: { children: React.ReactNode }) {
	return (
		<ProtectedComponent permission="admin.access">{children}</ProtectedComponent>
	);
}

/**
 * Component that only renders if user is super admin
 */
export function SuperAdminOnly({ children }: { children: React.ReactNode }) {
	return (
		<ProtectedComponent permission="admin.system">
			{children}
		</ProtectedComponent>
	);
}
